import {
  useState,
  useRef,
  useLayoutEffect,
  type CSSProperties,
  type KeyboardEvent as ReactKeyboardEvent,
  type ReactNode,
} from 'react'

export interface Column<Row> {
  key: string
  header: ReactNode
  align?: 'left' | 'right' | 'center'
  /** Render numbers in JetBrains Mono with tabular figures. Default true except the first column. */
  mono?: boolean
  /** Custom cell renderer; falls back to `row[key]`. */
  render?: (row: Row, index: number) => ReactNode
  width?: number | string
  minWidth?: number | string
  /** Make this column's header a sort control. */
  sortable?: boolean
  /** Value used when sorting by this column. Falls back to `row[key]`. */
  sortValue?: (row: Row) => string | number
  /**
   * Pin this column to the right edge so it stays visible when the table is
   * wider than its container. Use for a column of controls — a row action the
   * operator cannot see is a row action that does not exist. The cell paints an
   * opaque background so scrolled content passes underneath it.
   */
  stickyRight?: boolean
}

type Density = 'compact' | 'comfortable'
type SortDir = 'asc' | 'desc'

interface DataTableProps<Row> {
  columns: Column<Row>[]
  rows: Row[]
  rowKey?: (row: Row, index: number) => string | number
  onRowClick?: (row: Row, index: number) => void
  /** Static data-testid stamped on every row (e.g. legacy "priority-card"). */
  rowTestId?: string
  className?: string
  style?: CSSProperties
  empty?: ReactNode
  /** Row height / vertical padding. Horizontal padding + type size stay fixed. */
  density?: Density
  /** Column key to sort by on mount. */
  defaultSortKey?: string
  /** Initial direction for the default sort. Default 'asc'. */
  defaultSortDir?: SortDir
  /**
   * Opt-in row virtualization. When true the table renders only the rows in the
   * viewport (plus an overscan buffer) inside an owned, focusable scroll
   * container; sorting/row-click still operate on the FULL dataset. Fixed row
   * height (see DENSITY) makes this exact — the scrollbar length and position
   * match a fully-rendered table. Default false → byte-identical to before, so
   * the 14 non-virtualized call sites are untouched.
   */
  virtualize?: boolean
  /** Style applied to the virtualize scroll container (ignored when not virtualizing). */
  scrollStyle?: CSSProperties
  /**
   * Extra breathing room on the OUTER side of the first and last columns, on
   * top of the fixed 12px cell padding. A right-aligned money column one cell
   * padding away from a card border reads as pinned to the edge rather than as
   * laid out — /prices By-rival, founder review 2026-08-18. Default 0, so every
   * existing call site is byte-identical.
   */
  edgeGutter?: number
}

// Rows kept mounted above and below the visible window. Big enough to cover a
// fast flick before the next scroll event repaints, small enough to stay light.
const OVERSCAN = 12
// Viewport height guess used for the first paint only (before the scroll
// container is measured), so we never render the whole dataset even once.
const FALLBACK_VIEWPORT = 800

// Density → (row height, vertical cell padding). Horizontal padding is always
// 12px and font sizes never change — a denser table shrinks the row, not the type.
const DENSITY: Record<Density, { row: number; pad: number; headPad: number }> = {
  compact: { row: 30, pad: 6, headPad: 6 },
  comfortable: { row: 34, pad: 8, headPad: 7 },
}

const thBaseStyle: CSSProperties = {
  fontFamily: 'var(--mono)',
  // 9px uppercase mono at 0.10em tracking is the smallest text in the product
  // and it was painted in the faintest colour on the second-palest surface.
  // Founder review 2026-08-18 called the tables hard on the eyes; this is the
  // largest single contributor. A column header names the data under it — it is
  // structure, not marginalia, so it takes --text-2 (5.9:1) and one more pixel.
  fontSize: 10,
  fontWeight: 700,
  letterSpacing: '0.08em',
  textTransform: 'uppercase',
  color: 'var(--text-2)',
  whiteSpace: 'nowrap',
  // Sticky header — rides the top of the scroll container above the body cells.
  position: 'sticky',
  top: 0,
  zIndex: 2,
  // Was --surface-3, which in the light theme is #eef1f5 — the SAME value as
  // the page background. The header therefore read as a grey band running under
  // the table rather than as its top edge, and it is the main reason /prices
  // "still feels hard on the eyes" (founder review 2026-08-18). White ground,
  // one firm rule underneath: the table now starts where it looks like it does.
  background: 'var(--surface-solid)',
  borderBottom: '2px solid var(--border-2)',
}

function SortArrow({ dir }: { dir: SortDir }) {
  return (
    <span aria-hidden style={{ fontSize: 8, lineHeight: 1, color: 'var(--accent)' }}>
      {dir === 'asc' ? '▲' : '▼'}
    </span>
  )
}

/**
 * DataTable — the shared price/competitor table. Mono tabular numbers, sans for
 * the leading label column, soft mono uppercase headers. Sortable headers, a
 * sticky header row, and a compact/comfortable density. Aurora+ source:
 * `.price-table` (s12) and the price matrix in s07.
 */
export function DataTable<Row>({
  columns,
  rows,
  rowKey,
  onRowClick,
  rowTestId,
  className,
  style,
  empty,
  density = 'comfortable',
  defaultSortKey,
  defaultSortDir = 'asc',
  edgeGutter = 0,
  virtualize = false,
  scrollStyle,
}: DataTableProps<Row>) {
  // Uncontrolled internal sort — persists across parent re-renders (e.g. when the
  // parent hands down a freshly filtered `rows` array).
  const [sortKey, setSortKey] = useState<string | null>(defaultSortKey ?? null)
  const [sortDir, setSortDir] = useState<SortDir>(defaultSortDir)
  const [hoverKey, setHoverKey] = useState<string | null>(null)

  // Virtualization: the scroll container's live scrollTop + measured height drive
  // which slice of `displayRows` is mounted. Both are only meaningful (and only
  // read) when `virtualize` is on.
  const scrollRef = useRef<HTMLDivElement>(null)
  const [scrollTop, setScrollTop] = useState(0)
  const [viewportH, setViewportH] = useState(0)

  const d = DENSITY[density]

  useLayoutEffect(() => {
    if (!virtualize) return
    const el = scrollRef.current
    if (!el) return
    const measure = () => setViewportH(el.clientHeight)
    measure()
    // Track container resize (window resize, panel open, density change).
    const ro = new ResizeObserver(measure)
    ro.observe(el)
    return () => ro.disconnect()
  }, [virtualize])

  function handleSort(col: Column<Row>) {
    if (!col.sortable) return
    if (sortKey === col.key) {
      // Same column → reverse.
      setSortDir((dir) => (dir === 'asc' ? 'desc' : 'asc'))
    } else {
      // New column → ascending first.
      setSortKey(col.key)
      setSortDir('asc')
    }
  }

  const sortCol = sortKey ? columns.find((c) => c.key === sortKey) : undefined
  const displayRows = sortCol
    ? [...rows].sort((a, b) => {
        const va = sortCol.sortValue ? sortCol.sortValue(a) : (a as Record<string, unknown>)[sortCol.key]
        const vb = sortCol.sortValue ? sortCol.sortValue(b) : (b as Record<string, unknown>)[sortCol.key]
        let cmp: number
        if (typeof va === 'number' && typeof vb === 'number') cmp = va - vb
        else cmp = String(va ?? '').localeCompare(String(vb ?? ''))
        return sortDir === 'asc' ? cmp : -cmp
      })
    : rows

  // ── Virtual window ─────────────────────────────────────────────────────────
  // Slice the FULLY-sorted `displayRows` to just the rows near the viewport.
  // Fixed row height (d.row) → the top/bottom spacer rows reproduce the exact
  // scrollbar length and offset of a fully-rendered table.
  const total = displayRows.length
  let startIndex = 0
  let endIndex = total
  if (virtualize) {
    const effViewport = viewportH || FALLBACK_VIEWPORT
    const visible = Math.ceil(effViewport / d.row) + OVERSCAN * 2
    const first = Math.floor(scrollTop / d.row) - OVERSCAN
    // Clamp so a stale (too-large) scrollTop after the row set shrinks can't
    // push the window past the end.
    startIndex = Math.max(0, Math.min(first, Math.max(0, total - visible)))
    endIndex = Math.min(total, startIndex + visible)
  }
  const topPad = startIndex * d.row
  const bottomPad = Math.max(0, (total - endIndex) * d.row)
  const windowRows = virtualize ? displayRows.slice(startIndex, endIndex) : displayRows

  function handleKeyDown(e: ReactKeyboardEvent<HTMLDivElement>) {
    const el = scrollRef.current
    if (!el) return
    const page = Math.max(d.row, el.clientHeight - d.row)
    switch (e.key) {
      case 'ArrowDown': el.scrollTop += d.row; break
      case 'ArrowUp': el.scrollTop -= d.row; break
      case 'PageDown': el.scrollTop += page; break
      case 'PageUp': el.scrollTop -= page; break
      case 'Home': el.scrollTop = 0; break
      case 'End': el.scrollTop = el.scrollHeight; break
      default: return
    }
    e.preventDefault()
  }

  const head = (
    <thead>
      <tr>
        {columns.map((c, ci) => {
          const active = sortKey === c.key
          const showHoverHint = c.sortable && !active && hoverKey === c.key
          return (
            <th
              key={c.key}
              // Per-column class so responsive column visibility lives in CSS
              // (see [data-cols="dense"] in globals.css) rather than forcing
              // every dense page to rebuild its column array on resize.
              className={`col-${c.key}`}
              onClick={c.sortable ? () => handleSort(c) : undefined}
              onMouseEnter={c.sortable ? () => setHoverKey(c.key) : undefined}
              onMouseLeave={c.sortable ? () => setHoverKey(null) : undefined}
              style={{
                ...thBaseStyle,
                padding: `${d.headPad}px ${ci === columns.length - 1 ? 12 + edgeGutter : 12}px ${d.headPad}px ${ci === 0 ? 12 + edgeGutter : 12}px`,
                textAlign: c.align ?? 'left',
                width: c.width,
                minWidth: c.minWidth,
                cursor: c.sortable ? 'pointer' : 'default',
                userSelect: 'none',
                ...(c.stickyRight
                  ? {
                      position: 'sticky' as const, right: 0, zIndex: 7,
                      background: 'var(--surface-2)',
                      // Same hairline the pinned BODY cells carry. Without it
                      // the header column under the pin reads as sliced off
                      // rather than covered — visible on /prices at 1440.
                      boxShadow: 'inset 1px 0 0 var(--border)',
                    }
                  : null),
              }}
            >
              <span
                style={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  gap: 3,
                  width: c.align === 'right' || c.align === 'center' ? '100%' : undefined,
                  justifyContent:
                    c.align === 'right' ? 'flex-end' : c.align === 'center' ? 'center' : 'flex-start',
                }}
              >
                {c.header}
                {active && <SortArrow dir={sortDir} />}
                {showHoverHint && (
                  <span aria-hidden style={{ fontSize: 8, lineHeight: 1, color: 'var(--text-3)', opacity: 0.6 }}>
                    ▼
                  </span>
                )}
              </span>
            </th>
          )
        })}
      </tr>
    </thead>
  )

  // `i` is the GLOBAL row index (into displayRows), so the last-row border rule
  // and onRowClick stay correct even when only a window is mounted.
  const renderRow = (row: Row, i: number) => (
    <tr
      key={rowKey ? rowKey(row, i) : i}
      data-testid={rowTestId}
      onClick={onRowClick ? () => onRowClick(row, i) : undefined}
      style={{ cursor: onRowClick ? 'pointer' : undefined }}
    >
      {columns.map((c, ci) => {
        const mono = c.mono ?? ci !== 0
        return (
          <td
            key={c.key}
            className={`cell-${c.key}${mono ? ' tnum' : ''}`}
            style={{
              height: d.row,
              padding: `${d.pad}px ${ci === columns.length - 1 ? 12 + edgeGutter : 12}px ${d.pad}px ${ci === 0 ? 12 + edgeGutter : 12}px`,
              color: 'var(--text-1)',
              borderBottom: i === total - 1 ? 'none' : '1px solid var(--border)',
              fontFamily: mono ? 'var(--mono)' : 'var(--sans)',
              textAlign: c.align ?? 'left',
              ...(c.stickyRight
                ? {
                    position: 'sticky' as const,
                    right: 0,
                    zIndex: 2,
                    // Opaque, and matching the row — a translucent pinned cell
                    // would smear the columns sliding under it.
                    background: 'var(--surface-solid)',
                    boxShadow: 'inset 1px 0 0 var(--border)',
                  }
                : null),
            }}
          >
            {c.render ? c.render(row, i) : (row as Record<string, ReactNode>)[c.key]}
          </td>
        )
      })}
    </tr>
  )

  const emptyRow = total === 0 && (
    <tr>
      <td
        colSpan={columns.length}
        style={{ padding: '16px 12px', textAlign: 'center', color: 'var(--text-3)' }}
      >
        {empty ?? 'No rows'}
      </td>
    </tr>
  )

  const table = (
    <table
      className={className}
      style={{ width: '100%', borderCollapse: 'collapse', fontSize: 11.5, ...style }}
    >
      {head}
      <tbody>
        {emptyRow}
        {virtualize && topPad > 0 && (
          <tr aria-hidden>
            <td colSpan={columns.length} style={{ height: topPad, padding: 0, border: 'none' }} />
          </tr>
        )}
        {windowRows.map((row, wi) => renderRow(row, virtualize ? startIndex + wi : wi))}
        {virtualize && bottomPad > 0 && (
          <tr aria-hidden>
            <td colSpan={columns.length} style={{ height: bottomPad, padding: 0, border: 'none' }} />
          </tr>
        )}
      </tbody>
    </table>
  )

  if (!virtualize) return table

  return (
    <div
      ref={scrollRef}
      tabIndex={0}
      onScroll={(e) => setScrollTop(e.currentTarget.scrollTop)}
      onKeyDown={handleKeyDown}
      style={{ height: '100%', overflow: 'auto', outline: 'none', ...scrollStyle }}
    >
      {table}
    </div>
  )
}
